<template>
  <div class="w-full px-6 py-4">
    <div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.firstName')">
          <div
            v-if="_adminUser.role.isRoleTypeOwner"
            class="bg-gray-50 dark:bg-gray-800 rounded-md flex items-center justify-between px-3"
          >
            <p class="text-sm py-[10px]">
              {{ _adminUser.firstName || '-' }}
            </p>

            <o-button
              size="sm"
              icon="edit"
              transparent
              @click="handleChangeEmail"
            />
          </div>

          <text-field v-else v-model="_adminUser.firstName" :label="$t('general.firstName')" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.lastName')">
          <div
            v-if="_adminUser.role.isRoleTypeOwner"
            class="bg-gray-50 dark:bg-gray-800 rounded-md flex items-center justify-between px-3"
          >
            <p class="text-sm py-[10px]">
              {{ _adminUser.lastName || '-' }}
            </p>

            <o-button
              size="sm"
              icon="edit"
              transparent
              @click="handleChangeEmail"
            />
          </div>

          <text-field v-else v-model="_adminUser.lastName" :label="$t('general.lastName')" />
        </o-field-group>
      </div>

      <!-- Contact -->
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.email')">
          <div v-if="!_adminUser.isNewRecord" class="bg-gray-50 rounded-md flex items-center justify-between px-3 dark:bg-gray-900">
            <p class="text-sm text-gray-700 py-[10px] dark:text-gray-200">
              {{ email || '-' }}
            </p>

            <o-button v-if="_adminUser.allow.changeEmail" size="sm" icon="edit" transparent @click="handleChangeEmail" />
          </div>

          <text-field v-if="_adminUser.isNewRecord" v-model="_adminUser.email" type="email" icon="MailIcon" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.mobileCell')">
          <text-field v-model="_adminUser.phone" icon="SmartphoneIcon" />
        </o-field-group>
      </div>

      <!-- Address -->
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full" :label="$t('general.addressLine1')">
          <text-field v-model="_adminUser.address" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.city')">
          <text-field v-model="_adminUser.city" />
        </o-field-group>

        <o-field-group class="w-full md:w-1/2" :label="$t('general.region')">
          <text-field v-model="_adminUser.region" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.postcode')">
          <text-field v-model="_adminUser.postcode" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.label')">
          <text-field v-model="_adminUser.country" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full" :label="$t('general.bio')">
          <text-area-field v-model="_adminUser.bio" rows="6" class="text-sm text-gray-800 dark:text-gray-100" />
        </o-field-group>
      </div>

      <div v-if="_adminUser.isNewRecord" class="flex space-x-4 mb-4">
        <o-field-group
          :label="$t('general.role')"
          :name="$t('general.role')"
          :info="$t('components.adminUser.edit.details.selectRole')"
          class="w-full md:w-1/2"
        >
          <role-select v-model="_adminUser.role" />
        </o-field-group>
      </div>

      <o-custom-fields :data="_adminUser.customFields">
        <template #default="{fields}">
          <div
            v-for="field in fields"
            :key="field.props.id"
            class="flex space-x-4 mb-4"
          >
            <o-field-group class="w-full" :label="field.label">
              <component
                :is="field.component"
                v-bind="field.props"
                v-on="field.events"
              />
            </o-field-group>
          </div>
        </template>
      </o-custom-fields>
    </div>

    <change-email-modal
      ref="changeEmailModal"
      @success="handleChangeEmailSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import ChangeEmailModal from '@/components/adminUser/actions/ChangeEmail.vue'
import RoleSelect from '@/components/roles/elements/Select.vue'

import {
  OCustomFields,
  OAvatar,
} from '@/components/elements'

import {
  TextField,
  TextAreaField,
  ToggleField,
} from '@/components/fields'

interface Props {
  adminUser: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:adminUser', value: Record<string, any>): void
  (e: 'update'): void
}>()

const email = ref('')
const changeEmailModal = ref<any>()

const _adminUser = computed({
  get () {
    return props.adminUser
  },
  set (value) {
    emit('update:adminUser', value)
  },
})

watch(_adminUser, (adminUser) => {
  email.value = adminUser?.email
}, { immediate: true })

function handleChangeEmail () {
  changeEmailModal.value.open(_adminUser.value)
}

function handleChangeEmailSuccess (adminUser: any) {
  email.value = adminUser?.email
  emit('update')
}
</script>
